import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import { Popover } from "radix-ui";
import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

import { request } from "@/api/client";
import { cn } from "@/lib/cn";
import { formatDate, formatNumber, formatPct } from "@/lib/format";

import { Badge, type BadgeTone } from "./badge";
import { Spinner } from "./button";
import { Input } from "./input";

export type InstrumentKind = "equity" | "index" | "future" | "option";

/** One row of GET /instruments/search. */
export interface InstrumentHit {
  /** EXCHANGE:TRADING_SYMBOL, what alerts and quotes accept. */
  key: string;
  kind: InstrumentKind;
  exchange: string;
  /** The share, index or underlying (RELIANCE, NIFTY, GOLDM). */
  symbol: string;
  trading_symbol: string;
  name: string | null;
  /** "RELIANCE · Reliance Industries · NSE", "NIFTY 27 Nov 26 22600 CE · lot 65". */
  label: string;
  expiry: string | null;
  strike: number | null;
  option_type: "CE" | "PE" | null;
  lot_size: number | null;
  tick_size: number | null;
  ltp: number | null;
  change_pct: number | null;
}

const KIND_PILL: Record<InstrumentKind, { text: string; tone: BadgeTone }> = {
  equity: { text: "Share", tone: "neutral" },
  index: { text: "Index", tone: "brand" },
  future: { text: "FUT", tone: "accent" },
  option: { text: "OPT", tone: "violet" },
};

/** What the box shows once an instrument is picked: the label's first part. */
export function hitTitle(h: InstrumentHit): string {
  return h.label.split(" · ")[0] ?? h.symbol;
}

/** The rest of the label, with the exchange for contracts (shares carry it already). */
function hitDetail(h: InstrumentHit): string {
  const rest = h.label.split(" · ").slice(1);
  if (h.kind === "future" || h.kind === "option") rest.push(h.exchange);
  return rest.join(" · ");
}

function useSettled(value: string, ms: number): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
}

export interface SymbolPickerProps {
  /** The picked instrument; null while nothing (or only text) is chosen. */
  value: InstrumentHit | null;
  onChange: (hit: InstrumentHit | null) => void;
  /** Which kinds to offer (default: all four). */
  kinds?: readonly InstrumentKind[];
  /** Restrict to exchanges, e.g. ["NSE"] or ["NFO"]. */
  exchanges?: readonly string[];
  /** Drop rows the form can't use (e.g. alerts take only the NSE listing of a share). */
  filter?: (hit: InstrumentHit) => boolean;
  id?: string;
  /** The accessible name when no <label htmlFor> points at the box. */
  ariaLabel?: string;
  placeholder?: string;
  /** Text to start with (e.g. a saved draft's symbol). */
  defaultText?: string;
  autoFocus?: boolean;
  invalid?: boolean;
  className?: string;
  inputClassName?: string;
  limit?: number;
}

/**
 * Type a share, index, future or option and pick it from the list (an ARIA
 * combobox: arrows, Enter, Escape). The list comes from /instruments/search
 * as you type ("REL", "nifty fut", "NIFTY 22600 CE"), with each row's kind,
 * name or lot size, and last price. Typing again clears the pick, so a form
 * only ever holds an instrument that exists.
 */
export const SymbolPicker = forwardRef<HTMLInputElement, SymbolPickerProps>(
  function SymbolPicker(
    {
      value,
      onChange,
      kinds,
      exchanges,
      filter,
      id,
      ariaLabel,
      placeholder = "Type a symbol or name, e.g. RELIANCE, NIFTY FUT, NIFTY 22600 CE",
      defaultText,
      autoFocus,
      invalid,
      className,
      inputClassName,
      limit = 20,
    },
    ref,
  ) {
    const [text, setText] = useState(
      defaultText ?? (value ? hitTitle(value) : ""),
    );
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const listId = useId();
    const lastKey = useRef<string | null>(value?.key ?? null);
    const anchor = useRef<HTMLDivElement>(null);

    // A pick made (or cleared) by the form itself, e.g. after sending: follow it.
    useEffect(() => {
      const key = value?.key ?? null;
      if (key === lastKey.current) return;
      lastKey.current = key;
      setText(value ? hitTitle(value) : "");
    }, [value]);

    const q = useSettled(text.trim(), 150);
    const kindsParam = kinds?.join(",");
    const exParam = exchanges?.join(",");
    const enabled = open && q.length > 0 && !(value && q === hitTitle(value));
    const search = useQuery({
      queryKey: [
        "/instruments/search",
        { q, kinds: kindsParam, exchange: exParam, limit },
      ],
      queryFn: ({ signal }) =>
        request<InstrumentHit[]>("get", "/instruments/search", {
          query: { q, kinds: kindsParam, exchange: exParam, limit },
          signal,
        }),
      enabled,
      staleTime: 30_000,
      placeholderData: keepPreviousData,
    });
    const typed = text.trim();
    const hits =
      typed && enabled
        ? (search.data ?? []).filter((h) => !filter || filter(h))
        : [];
    const waiting = q !== typed || search.isFetching;
    const showList =
      open && typed.length > 0 && !(value && typed === hitTitle(value));

    function pick(h: InstrumentHit) {
      lastKey.current = h.key;
      setText(hitTitle(h));
      setOpen(false);
      setActive(0);
      onChange(h);
    }

    function clear() {
      lastKey.current = null;
      setText("");
      setActive(0);
      onChange(null);
    }

    function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
        setActive((a) => Math.min(a + 1, Math.max(hits.length - 1, 0)));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((a) => Math.max(a - 1, 0));
      } else if (e.key === "Enter") {
        const h = showList ? (hits[active] ?? hits[0]) : undefined;
        if (h && !waiting) {
          e.preventDefault();
          pick(h);
        } else if (showList) {
          e.preventDefault(); // don't submit the form with a half-typed symbol
        }
      } else if (e.key === "Escape" && showList) {
        e.preventDefault();
        e.stopPropagation(); // close the list, not the dialog around it
        setOpen(false);
      }
    }

    const optionId = (i: number) => `${listId}-o${i}`;

    return (
      <Popover.Root open={showList} onOpenChange={(o) => !o && setOpen(false)}>
        <Popover.Anchor asChild>
          <div
            ref={anchor}
            className={cn("relative w-full min-w-0", className)}
          >
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-subtle"
              aria-hidden
            />
            <Input
              ref={ref}
              id={id}
              role="combobox"
              aria-label={ariaLabel}
              aria-expanded={showList}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-invalid={invalid || undefined}
              aria-activedescendant={
                showList && hits[active] ? optionId(active) : undefined
              }
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              autoFocus={autoFocus}
              className={cn(
                "pr-9 pl-9 font-semibold placeholder:font-normal",
                inputClassName,
              )}
              placeholder={placeholder}
              value={text}
              maxLength={60}
              onChange={(e) => {
                setText(e.target.value);
                setOpen(true);
                setActive(0);
                if (value) {
                  lastKey.current = null;
                  onChange(null);
                }
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
              onKeyDown={onKeyDown}
            />
            {showList && waiting ? (
              <Spinner className="absolute top-1/2 right-3 size-4 -translate-y-1/2 text-subtle" />
            ) : text ? (
              <button
                type="button"
                aria-label="Clear the symbol"
                onMouseDown={(e) => e.preventDefault()}
                onClick={clear}
                className="absolute top-1/2 right-2 grid size-6 -translate-y-1/2 place-items-center rounded-md text-subtle hover:bg-surface-2 hover:text-fg"
              >
                <X className="size-4" aria-hidden />
              </button>
            ) : null}
          </div>
        </Popover.Anchor>
        <Popover.Portal>
          <Popover.Content
            side="bottom"
            align="start"
            sideOffset={4}
            collisionPadding={8}
            // the typing stays in the box; the list never takes the focus
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              if (anchor.current?.contains(e.target as Node))
                e.preventDefault();
            }}
            className="z-[60] w-[var(--radix-popover-trigger-width)] min-w-64 rounded-lg border border-border bg-surface p-1 shadow-card"
          >
            <ul
              id={listId}
              role="listbox"
              aria-label="Matching instruments"
              className="max-h-[min(20rem,var(--radix-popover-content-available-height))] overflow-y-auto overscroll-contain"
            >
              {hits.length === 0 ? (
                <li
                  className="px-3 py-2.5 text-sm text-muted"
                  role="presentation"
                >
                  {waiting
                    ? "Searching…"
                    : "No matching instrument. Try the symbol, e.g. RELIANCE or NIFTY FUT."}
                </li>
              ) : (
                hits.map((h, i) => {
                  const pill = KIND_PILL[h.kind];
                  return (
                    <li
                      key={h.key}
                      id={optionId(i)}
                      role="option"
                      aria-selected={i === active}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseMove={() => i !== active && setActive(i)}
                      onClick={() => pick(h)}
                      className={cn(
                        "flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm",
                        i === active && "bg-surface-2",
                      )}
                    >
                      <Badge
                        tone={pill.tone}
                        className="w-11 shrink-0 justify-center"
                      >
                        {h.kind === "option"
                          ? (h.option_type ?? pill.text)
                          : pill.text}
                      </Badge>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-semibold text-fg">
                          {hitTitle(h)}
                        </span>
                        <span className="block truncate text-xs text-muted">
                          {hitDetail(h)}
                        </span>
                      </span>
                      {h.ltp != null ? (
                        <span className="num shrink-0 text-right text-xs">
                          <span className="block font-semibold text-fg">
                            {formatNumber(h.ltp)}
                          </span>
                          {h.change_pct != null ? (
                            <span
                              className={cn(
                                "block",
                                h.change_pct >= 0 ? "text-up" : "text-down",
                              )}
                            >
                              {formatPct(h.change_pct)}
                            </span>
                          ) : null}
                        </span>
                      ) : null}
                    </li>
                  );
                })
              )}
            </ul>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    );
  },
);

/** The picked contract's expiry, strike and type (they come from the pick, not typing). */
export function PickedContract({ pick }: { pick: InstrumentHit | null }) {
  const facts: [string, string][] = pick
    ? [
        ["Expiry", pick.expiry ? formatDate(pick.expiry, "d MMM yyyy") : "—"],
        ...(pick.kind === "option"
          ? ([
              ["Strike", pick.strike != null ? formatNumber(pick.strike, 2, 0) : "—"],
              ["Type", pick.option_type ?? "—"],
            ] as [string, string][])
          : []),
      ]
    : [];
  return (
    <div className="grid gap-1.5">
      <span className="text-xs font-medium text-muted">Contract</span>
      <div className="flex min-h-10 flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-border bg-surface-2/60 px-3 py-2 text-sm">
        {pick ? (
          facts.map(([k, v]) => (
            <span key={k} className="whitespace-nowrap">
              <span className="text-muted">{k}</span> <b className="num font-semibold text-fg">{v}</b>
            </span>
          ))
        ) : (
          <span className="text-subtle">Expiry, strike and type come from the pick</span>
        )}
      </div>
    </div>
  );
}
